import React, { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Card, Result, Button, message, Spin } from 'antd';
import axios from 'axios';
import config from '../config';
import useActivePeriod from '../hooks/useActivePeriod';

const EmailVerification = () => {
    const { id, hash } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { hasActivePeriod } = useActivePeriod();
    const [verificationStatus, setVerificationStatus] = useState('verifying');
    const [alreadyVerified, setAlreadyVerified] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    // Guard against React StrictMode double-invocation and user refresh
    const hasCalledRef = useRef(false);

    useEffect(() => {
        if (hasCalledRef.current) return;
        hasCalledRef.current = true;

        const verifyEmail = async () => {
            try {
                if (!id || !hash) {
                    throw new Error('Link xác thực không hợp lệ!');
                }

                const expires = searchParams.get('expires');
                const signature = searchParams.get('signature');

                if (!expires || !signature) {
                    throw new Error('Link xác thực không hợp lệ hoặc đã hết hạn!');
                }

                const response = await axios.get(`${config.apiUrl}/api/v1/verify-email/${id}/${hash}`, {
                    params: { expires, signature },
                    timeout: 10000,
                });

                if (response.data.success) {
                    setAlreadyVerified(!!response.data.already_verified);
                    setVerificationStatus('success');
                    if (!response.data.already_verified) {
                        message.success('Xác thực email thành công!');
                    }
                    setTimeout(() => navigate('/login'), 3000);
                } else {
                    throw new Error(response.data.message || 'Xác thực email thất bại!');
                }
            } catch (error) {
                console.error('Verification error:', error);
                setVerificationStatus('error');

                if (axios.isAxiosError(error)) {
                    if (error.code === 'ECONNABORTED') {
                        setErrorMessage('Yêu cầu xác thực đã hết thời gian. Vui lòng thử lại!');
                    } else if (!error.response) {
                        setErrorMessage('Không thể kết nối đến máy chủ. Vui lòng kiểm tra kết nối mạng!');
                    } else {
                        setErrorMessage(error.response?.data?.message || 'Có lỗi xảy ra khi xác thực email!');
                    }
                } else {
                    setErrorMessage(error.message || 'Có lỗi xảy ra khi xác thực email!');
                }
            } finally {
                setIsLoading(false);
            }
        };

        verifyEmail();
    }, [id, hash, searchParams, navigate]);

    if (verificationStatus === 'verifying') {
        return (
            <Card style={{ maxWidth: 600, margin: '0 auto', marginTop: 20 }}>
                <Result
                    status="info"
                    title="Đang xác thực email..."
                    subTitle="Vui lòng đợi trong giây lát."
                    icon={isLoading && <Spin size="large" />}
                />
            </Card>
        );
    }

    if (verificationStatus === 'error') {
        return (
            <Card style={{ maxWidth: 600, margin: '0 auto', marginTop: 20 }}>
                <Result
                    status="error"
                    title="Xác thực thất bại!"
                    subTitle={errorMessage}
                    extra={[
                        <Button type="primary" key="login" onClick={() => navigate('/login')}>
                            Đến trang đăng nhập
                        </Button>,
                        hasActivePeriod && (
                            <Button key="register" onClick={() => navigate('/register')}>
                                Đăng ký lại
                            </Button>
                        ),
                        <Button key="retry" onClick={() => window.location.reload()}>
                            Thử lại
                        </Button>
                    ].filter(Boolean)}
                />
            </Card>
        );
    }

    return (
        <Card style={{ maxWidth: 600, margin: '0 auto', marginTop: 20 }}>
            <Result
                status="success"
                title={alreadyVerified ? 'Email đã được xác thực!' : 'Xác thực thành công!'}
                subTitle={
                    <div>
                        <p>
                            {alreadyVerified
                                ? 'Email của bạn đã được xác thực trước đó.'
                                : 'Email của bạn đã được xác thực thành công!'}
                        </p>
                        <p>Bạn sẽ được chuyển hướng đến trang đăng nhập trong giây lát...</p>
                    </div>
                }
            />
        </Card>
    );
};

export default EmailVerification;